/**
 * Autore: Salvo de Luca
 * Azienda Dialogicom s.r.l.s.
 * File: src/components/forms/utils/validators.ts
 * Release: v1.0.0
 * Data: 28/05/2025
 */

/**
 * Valida un campo obbligatorio
 */
export function validateRequired(value: any): string | undefined {
  if (value === undefined || value === null || value === '') {
    return 'Campo obbligatorio';
  }
  return undefined;
}

/**
 * Valida una email
 */
export function validateEmail(value: string): string | undefined {
  if (!value) return undefined;
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(value)) {
    return 'Email non valida';
  }
  return undefined;
}

/**
 * Valida un numero di telefono
 */
export function validatePhone(value: string): string | undefined {
  if (!value) return undefined;
  
  // Rimuove spazi e altri caratteri non numerici
  const cleaned = value.replace(/[\s\-\(\)]/g, '');
  
  // Verifica la lunghezza (8-15 cifre)
  if (!/^\+?[0-9]{8,15}$/.test(cleaned)) {
    return 'Numero di telefono non valido';
  }
  return undefined;
}

/**
 * Valida un URL
 */
export function validateUrl(value: string): string | undefined {
  if (!value) return undefined;
  
  try {
    new URL(value);
    return undefined;
  } catch {
    return 'URL non valido';
  }
}

/**
 * Valida una lunghezza minima
 */
export function validateMinLength(minLength: number) {
  return (value: string): string | undefined => {
    if (!value) return undefined;
    
    if (value.length < minLength) {
      return `Deve contenere almeno ${minLength} caratteri`;
    }
    return undefined;
  };
}

/**
 * Valida una lunghezza massima
 */
export function validateMaxLength(maxLength: number) {
  return (value: string): string | undefined => {
    if (!value) return undefined;
    
    if (value.length > maxLength) {
      return `Non può superare ${maxLength} caratteri`;
    }
    return undefined;
  };
}

/**
 * Valida un pattern
 */
export function validatePattern(pattern: RegExp, message?: string) {
  return (value: string): string | undefined => {
    if (!value) return undefined;
    
    if (!pattern.test(value)) {
      return message || 'Formato non valido';
    }
    return undefined;
  };
}

/**
 * Valida un Cap italiano (5 cifre)
 */
export function validateItalianZip(value: string): string | undefined {
  if (!value) return undefined;
  
  if (!/^[0-9]{5}$/.test(value)) {
    return 'CAP deve essere di 5 cifre';
  }
  return undefined;
}

/**
 * Valida una provincia italiana (2 lettere maiuscole)
 */
export function validateItalianProvince(value: string): string | undefined {
  if (!value) return undefined;
  
  if (!/^[A-Z]{2}$/.test(value)) {
    return 'Provincia deve essere di 2 lettere maiuscole';
  }
  return undefined;
}

/**
 * Valida una partita IVA italiana (11 cifre con checkdigit)
 */
export function validatePartitaIva(value: string): string | undefined {
  if (!value) return undefined;
  
  // Rimuove spazi e altri caratteri non numerici
  const cleaned = value.replace(/\D/g, '');
  
  // Verifica la lunghezza
  if (cleaned.length !== 11) {
    return 'Partita IVA deve essere di 11 cifre';
  }
  
  // Verifica che contenga solo numeri
  if (!/^[0-9]{11}$/.test(cleaned)) {
    return 'Partita IVA deve contenere solo numeri';
  }
  
  // Algoritmo di controllo checkdigit
  const digits = cleaned.split('').map(Number);
  
  // Calcolo del checkdigit con pesi 1,2,1,2,1,2,1,2,1,2
  const weights = [1, 2, 1, 2, 1, 2, 1, 2, 1, 2];
  let sum = 0;
  
  for (let i = 0; i < 10; i++) {
    let product = digits[i] * weights[i];
    if (product > 9) {
      product = Math.floor(product / 10) + (product % 10);
    }
    sum += product;
  }
  
  const remainder = sum % 10;
  const expectedCheckDigit = remainder === 0 ? 0 : 10 - remainder;
  
  if (expectedCheckDigit !== digits[10]) {
    return 'Partita IVA non valida (checkdigit errato)';
  }
  
  return undefined; // Valido
}

/**
 * Valida un codice fiscale italiano (inclusi nati all'estero)
 */
export function validateCodiceFiscale(value: string): string | undefined {
  if (!value) return undefined;
  
  // Normalizza: maiuscolo e senza spazi
  const cleaned = value.replace(/\s/g, '').toUpperCase();
  
  // Verifica la lunghezza
  if (cleaned.length !== 16) {
    return 'Codice fiscale deve essere di 16 caratteri';
  }
  
  // Verifica il formato di base (inclusi nati all'estero con Z)
  if (!/^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/.test(cleaned)) {
    return 'Formato codice fiscale non valido';
  }
  
  // Tabelle per il calcolo del check digit
  const oddValues: { [key: string]: number } = {
    '0': 1, '1': 0, '2': 5, '3': 7, '4': 9, '5': 13, '6': 15, '7': 17, '8': 19, '9': 21,
    'A': 1, 'B': 0, 'C': 5, 'D': 7, 'E': 9, 'F': 13, 'G': 15, 'H': 17, 'I': 19, 'J': 21,
    'K': 2, 'L': 4, 'M': 18, 'N': 20, 'O': 11, 'P': 3, 'Q': 6, 'R': 8, 'S': 12, 'T': 14,
    'U': 16, 'V': 10, 'W': 22, 'X': 25, 'Y': 24, 'Z': 23
  };
  
  const evenValues: { [key: string]: number } = {
    '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
    'A': 0, 'B': 1, 'C': 2, 'D': 3, 'E': 4, 'F': 5, 'G': 6, 'H': 7, 'I': 8, 'J': 9,
    'K': 10, 'L': 11, 'M': 12, 'N': 13, 'O': 14, 'P': 15, 'Q': 16, 'R': 17, 'S': 18, 'T': 19,
    'U': 20, 'V': 21, 'W': 22, 'X': 23, 'Y': 24, 'Z': 25
  };
  
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    const char = cleaned[i];
    if (i % 2 === 0) {
      sum += oddValues[char] || 0;
    } else {
      sum += evenValues[char] || 0;
    }
  }
  
  const remainder = sum % 26;
  const expectedCheck = String.fromCharCode('A'.charCodeAt(0) + remainder);
  
  if (expectedCheck !== cleaned[15]) {
    return 'Codice fiscale non valido';
  }
  
  return undefined; // Valido
}

/**
 * Valida un codice univoco (5 o 7 caratteri alfanumerici)
 */
export function validateCodiceUnivoco(value: string): string | undefined {
  if (!value) return undefined;
  
  // Normalizza: maiuscolo e senza spazi
  const cleaned = value.replace(/\s/g, '').toUpperCase();
  
  // Verifica la lunghezza (5 o 7 caratteri)
  if (cleaned.length !== 5 && cleaned.length !== 7) {
    return 'Codice univoco deve essere di 5 o 7 caratteri';
  }
  
  // Verifica che contiene solo lettere e numeri
  if (!/^[A-Z0-9]+$/.test(cleaned)) {
    return 'Codice univoco deve contenere solo lettere e numeri';
  }
  
  return undefined;
}

/**
 * Gruppo di validatori predefiniti
 */
export const validators = {
  required: validateRequired,
  email: validateEmail,
  phone: validatePhone,
  url: validateUrl,
  minLength: validateMinLength,
  maxLength: validateMaxLength,
  pattern: validatePattern,
  italianZip: validateItalianZip,
  italianProvince: validateItalianProvince,
  partitaIva: validatePartitaIva,
  codiceFiscale: validateCodiceFiscale,
  codiceUnivoco: validateCodiceUnivoco
}; 